"use client";

import { useState, useEffect, useRef } from "react";
import { Smartphone, Share, Plus, MoreVertical } from "lucide-react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

export default function InstallAppButton() {
  const installPromptRef = useRef<BeforeInstallPromptEvent | null>(null);
  const [canInstall, setCanInstall] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showHint, setShowHint] = useState<"ios" | "other" | null>(null);
  const [isIOS, setIsIOS] = useState(false);

  // Register service worker so PWA install prompt can fire (Chrome/Edge)
  useEffect(() => {
    if (typeof window === "undefined" || !("serviceWorker" in navigator)) return;
    navigator.serviceWorker
      .register("/sw.js")
      .then(() => {})
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as { standalone?: boolean }).standalone === true;
    if (standalone) {
      setIsInstalled(true);
      return;
    }

    const ua = navigator.userAgent;
    const ios = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
    setIsIOS(ios);

    const handler = (e: Event) => {
      e.preventDefault();
      installPromptRef.current = e as BeforeInstallPromptEvent;
      setCanInstall(true);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      installPromptRef.current = null;
    };
  }, []);

  const handleInstall = async () => {
    const promptEvent = installPromptRef.current;
    if (promptEvent) {
      try {
        await promptEvent.prompt();
        const { outcome } = await promptEvent.userChoice;
        if (outcome === "accepted") {
          installPromptRef.current = null;
          setCanInstall(false);
        }
      } catch {
        setShowHint("other");
      }
      return;
    }
    if (isIOS) {
      setShowHint("ios");
    } else {
      setShowHint("other");
    }
  };

  if (isInstalled) return null;

  return (
    <>
      <button
        type="button"
        onClick={handleInstall}
        className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-white/20 text-white/90 font-medium hover:bg-white/10 hover:text-white transition-all duration-200 text-sm"
        aria-label="Download as app"
      >
        <Smartphone className="w-4 h-4" />
        Download as app
      </button>
      {showHint === "ios" && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 pb-12 bg-black/60"
          onClick={() => setShowHint(null)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm shadow-xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-900 dark:text-white font-semibold mb-2">Add to Home Screen</p>
            <ol className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-decimal list-inside">
              <li className="flex items-center gap-2">
                <Share className="w-4 h-4 shrink-0" />
                Tap the Share button in Safari
              </li>
              <li className="flex items-center gap-2">
                <Plus className="w-4 h-4 shrink-0" />
                Scroll and tap &quot;Add to Home Screen&quot;
              </li>
            </ol>
            <button
              type="button"
              onClick={() => setShowHint(null)}
              className="mt-4 w-full py-2 rounded-xl bg-indigo-600 text-white font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}
      {showHint === "other" && (
        <div
          className="fixed inset-0 z-[100] flex items-end justify-center p-4 pb-12 bg-black/60"
          onClick={() => setShowHint(null)}
          role="presentation"
        >
          <div
            className="bg-white dark:bg-gray-900 rounded-2xl p-6 max-w-sm shadow-xl border border-white/10"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-gray-900 dark:text-white font-semibold mb-2">Install the app</p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Use your browser menu to add Mindaloo to your home screen or install it:
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2 list-disc list-inside">
              <li className="flex items-center gap-2">
                <MoreVertical className="w-4 h-4 shrink-0" />
                Chrome/Edge: Menu (⋮) → &quot;Install app&quot; or &quot;Add to Home screen&quot;
              </li>
              <li>Safari (iOS): Share → Add to Home Screen</li>
            </ul>
            <button
              type="button"
              onClick={() => setShowHint(null)}
              className="mt-4 w-full py-2 rounded-xl bg-indigo-600 text-white font-medium"
            >
              Got it
            </button>
          </div>
        </div>
      )}
    </>
  );
}
